import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Spinner,
  Table,
} from 'react-bootstrap';
import { CheckCircle, Download, FileExcelFill } from 'react-bootstrap-icons';
import {
  downloadInventoryListTemplate,
  INVENTORY_LIST_MAX_ROWS,
  validateInventoryProductListFile,
} from '../../../utils/inventoryProductListExcelValidator';
import { logInfo, logWarn } from '../../../config_and_helpers/logger';

const LOG_CONTEXT = 'ImportFormatGuide';

/**
 * Step A: format requirements, template download, client-side file validation.
 *
 * @param {Object} props
 * @param {File|null} props.selectedFile
 * @param {Function} props.onFileChange - (file, validationResult) => void
 * @param {Object|null} props.validationResult - Last validation from parent
 * @param {boolean} props.disabled
 * @param {Function} [props.onExportInventory] - Triggers API export of full inventory
 * @param {boolean} [props.isExporting]
 * @param {string} [props.exportError]
 */
const ImportFormatGuide = ({
  selectedFile,
  onFileChange,
  validationResult,
  disabled = false,
  onExportInventory,
  isExporting = false,
  exportError = '',
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleFileSelect = async event => {
    const file = event.target.files?.[0] ?? null;
    setLocalError('');
    if (!file) {
      onFileChange(null, null);
      return;
    }
    setIsValidating(true);
    logInfo(LOG_CONTEXT, 'validating file', { fileName: file.name });
    try {
      const result = await validateInventoryProductListFile(file);
      if (!result.valid) {
        logWarn(LOG_CONTEXT, 'validation failed', { errors: result.errors });
      }
      onFileChange(file, result);
    } catch (err) {
      logWarn(LOG_CONTEXT, 'validation exception', err);
      setLocalError('Validation failed unexpectedly. Please try another file.');
      onFileChange(file, { valid: false, errors: [err.message], warnings: [], meta: {} });
    } finally {
      setIsValidating(false);
      event.target.value = '';
    }
  };

  const valid = validationResult?.valid === true;

  return (
    <div className="mb-4">
      <h5 className="mb-3">
        <FileExcelFill className="me-2 text-success" />
        Excel format
      </h5>
      <p className="text-muted">
        Upload a catalogue list to match against inventory products. Only
        inventory matches can be linked to a project or included in an order
        request.
      </p>

      <Table bordered size="sm" className="mb-3 bg-white">
        <thead>
          <tr>
            <th>Column</th>
            <th>Required</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>CAT#</strong>
            </td>
            <td>Yes</td>
            <td>Matched to product catalogue number (case-insensitive)</td>
          </tr>
          <tr>
            <td>Product name</td>
            <td>No</td>
            <td>Shown in preview only</td>
          </tr>
          <tr>
            <td>Quantity</td>
            <td>No</td>
            <td>Integer ≥ 1; defaults to 1 if empty</td>
          </tr>
        </tbody>
      </Table>

      <ul className="text-muted small">
        <li>File format: <strong>.xlsx</strong> only</li>
        <li>Header row: row 1; data from row 2</li>
        <li>Maximum {INVENTORY_LIST_MAX_ROWS} non-empty CAT# rows</li>
        <li>Empty CAT# rows are skipped</li>
      </ul>

      <div className="mb-3">
        <Button
          variant="outline-secondary"
          size="sm"
          className="me-2 mb-2"
          onClick={() => downloadInventoryListTemplate()}
          disabled={disabled}
        >
          <Download className="me-1" />
          Download template
        </Button>
        {onExportInventory ? (
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2 mb-2"
            onClick={onExportInventory}
            disabled={disabled || isExporting}
          >
            {isExporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="me-1" />
                Export inventory list
              </>
            )}
          </Button>
        ) : null}
      </div>
      <p className="text-muted small mb-3">
        <strong>Download template</strong> is an empty example file.{' '}
        <strong>Export inventory list</strong> downloads the current inventory
        from the server (no row limit) for offline editing and re-import.
      </p>

      {exportError ? (
        <Alert variant="danger" className="mb-3">
          {exportError}
        </Alert>
      ) : null}

      <Form.Group className="mb-2">
        <Form.Label>Select file</Form.Label>
        <Form.Control
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileSelect}
          disabled={disabled || isValidating}
        />
      </Form.Group>

      {isValidating ? (
        <div className="d-flex align-items-center text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Checking file format…
        </div>
      ) : null}

      {localError ? <Alert variant="danger">{localError}</Alert> : null}

      {validationResult && !validationResult.valid ? (
        <Alert variant="danger">
          <strong>File cannot be uploaded:</strong>
          <ul className="mb-0 mt-2">
            {validationResult.errors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {validationResult?.warnings?.length > 0 ? (
        <Alert variant="warning">
          <ul className="mb-0">
            {validationResult.warnings.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {valid ? (
        <Alert variant="success" className="d-flex align-items-start">
          <CheckCircle className="me-2 flex-shrink-0 mt-1" />
          <div>
            <strong>{selectedFile?.name}</strong> passed format checks.
            <div className="small mt-1">
              {validationResult.meta.rowCount} row(s) with CAT#.
              {validationResult.meta.detectedColumns?.cat_num ? (
                <>
                  {' '}
                  Detected columns: CAT# = &quot;
                  {validationResult.meta.detectedColumns.cat_num}
                  &quot;
                  {validationResult.meta.detectedColumns.name
                    ? `, Name = "${validationResult.meta.detectedColumns.name}"`
                    : ''}
                  {validationResult.meta.detectedColumns.quantity
                    ? `, Quantity = "${validationResult.meta.detectedColumns.quantity}"`
                    : ''}
                  .
                </>
              ) : null}
            </div>
          </div>
        </Alert>
      ) : null}
    </div>
  );
};

export default ImportFormatGuide;
